import React, {useContext, useEffect, useMemo, useRef, useState} from "react";
import {useFeatureFlags} from "utilities/featureUtil";
import {useSelector} from "react-redux";
import {buildStyles} from "connectui/component/buildStyles";
import {useTheme} from "connectui/theme/ThemeProvider";
import ICONS from "connectui/icons/iconMap";
import {MessagesContext} from "../../panel/conversations/ConversationsPanel";
import {getAvailableChannels} from "utilities/helperFunctions";

import FlexContainer from "connectui/component/layout/FlexContainer";
import ButtonIcon from "connectui/component/input/button/ButtonIcon";
import SvgIcon from "connectui/component/display/SvgIcon";
import ChannelSelectorModal from "../modal/ChannelSelectorModal";

const useStyles = (theme) =>
  buildStyles({
    channelIcon: {
      width: "20px",
      height: "20px",
      justifyContent: "center",
    },
  });

const MobileChannelRow = ({
  guestId,
  disabled,
  activeChannel,
  setActiveChannel,
  whatsappData,
  setAvailableChannels,
  allChannelsDisabled,
  lastMessage,
}) => {
  const {theme} = useTheme();
  const classes = useStyles(theme);
  const {lodgifyMessaging, lodgifyOta, hostawayOta} = useFeatureFlags();
  const {replyEmailProps} = useContext(MessagesContext);
  const channel = useRef(null);
  const guests = useSelector((state) => state.defaultReducer.guests_dict);
  const user_profile = useSelector(
    (state) => state.defaultReducer.user_profile,
  );
  const guestBookings = useSelector(
    (state) => state.defaultReducer.selected_guest_bookings,
  );
  const [channelSelectorOpen, setChannelSelectorOpen] = useState(false);
  const [isChannelSelected, setIsChannelSelected] = useState(false);
  const [tempChannelID, setTempChannelID] = useState(null);
  const guest = guests[guestId] ?? {};
  const isNote = activeChannel === "note";

  // Reset channel selection flag when changing conversation
  useEffect(() => setIsChannelSelected(false), [guestId]);

  useEffect(() => {
    if (replyEmailProps.enabled) setIsChannelSelected(true);
  }, [replyEmailProps]);

  const availableChannels = useMemo(
    () =>
      getAvailableChannels({
        guest,
        flags: {lodgifyMessaging, lodgifyOta, hostawayOta},
        guestBookings,
        user_profile,
        whatsappData,
      }),
    [
      guest,
      user_profile,
      guestBookings,
      lodgifyMessaging,
      lodgifyOta,
      hostawayOta,
      whatsappData,
    ],
  );
  const selectedChannel =
    availableChannels.find(
      (c) => c.id === (isNote ? channel.current : activeChannel),
    ) ?? {};

  useEffect(() => {
    if (activeChannel === "note") return;
    channel.current = activeChannel;
  }, [activeChannel]);

  useEffect(() => {
    setAvailableChannels(availableChannels);
  }, [availableChannels]);

  useEffect(() => {
    // User has already selected a channel
    if (isChannelSelected || disabled) return;
    let newChannel = null;

    // Use the channel of the last message if available and valid
    if (
      lastMessage.channel &&
      availableChannels.some((c) => c.id === lastMessage.channel)
    ) {
      newChannel = lastMessage.channel;
    } else {
      // Select the first available channel
      newChannel = availableChannels.find((c) => c.show)?.id || null;
    }

    if (activeChannel !== newChannel) {
      setActiveChannel(newChannel);
    }
  });

  const handleChannelClick = (channelId) => {
    setIsChannelSelected(true);
    setActiveChannel(channelId);
  };

  return (
    <FlexContainer gap={theme.spacing(2)}>
      <ChannelSelectorModal
        isOpen={channelSelectorOpen}
        onClose={() => setChannelSelectorOpen(false)}
        availableChannels={availableChannels}
        selectedChannel={tempChannelID}
        setSelectedChannel={setTempChannelID}
        handleChannelClick={handleChannelClick}
      />
      <ButtonIcon
        disabled={allChannelsDisabled}
        icon={
          <SvgIcon
            component={ICONS[selectedChannel.icon]}
            className={classes.channelIcon}
            sx={{
              color: isNote ? theme.colors.gray500 : selectedChannel.color,
            }}
          />
        }
        sx={{
          backgroundColor: isNote
            ? theme.colors.warning100
            : theme.colors.gray100,
          width: "32px",
          height: "32px",
          borderRadius: "50%",
          padding: 0,
          "&:hover": {backgroundColor: theme.colors.gray200},
        }}
        onClick={() => {
          if (isNote) {
            handleChannelClick(selectedChannel.id);
          } else {
            setTempChannelID(selectedChannel.id);
            setChannelSelectorOpen(true);
          }
        }}
      />
      <ButtonIcon
        disabled={allChannelsDisabled}
        icon={
          <SvgIcon
            component={ICONS.note}
            className={classes.noteIcon}
            sx={{
              width: "20px",
              height: "20px",
              color: isNote ? theme.colors.warning600 : theme.colors.gray600,
            }}
          />
        }
        sx={{
          backgroundColor: isNote
            ? theme.colors.warning100
            : theme.colors.gray100,
          border: isNote ? `1px solid ${theme.colors.warning600}` : "none",
          width: "32px",
          height: "32px",
          borderRadius: "50%",
          padding: theme.spacing(2),
          "&:hover": {backgroundColor: theme.colors.gray200},
        }}
        onClick={() => handleChannelClick("note")}
      />
    </FlexContainer>
  );
};

export default MobileChannelRow;
